<script lang="ts">
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import TextImageSidesStructure, {
		ItemDescription,
		ItemNativeName,
		ItemQuickFacts,
		ItemTitle,
		ItemViewPrimary
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import Story from '$lib/components/ui/Story.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { shouldDisplayStoryElements, type HiddenGem } from '$lib/features/hiddenGem/hiddenGem.type';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import {
		buildExampleItemsPhrase,
		closestByLength,
		findFirstAvailableImage,
		sortShortestToLongest
	} from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { secondsToFormattedHourMinutes } from '$lib/utils/time.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconClockTimeFourOutline from '~icons/mdi/clock-time-four-outline';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.HIDDEN_GEM;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<HiddenGem[]>(output.items);
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(items.map((item) => item.feature.englishName));
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: ImageDetails | undefined = findFirstAvailableImage(items);

		interface Limits {
			exampleItems: number;
		}
		function examplesLimits(limits: Limits): {
			exampleItems: string;
		} {
			return {
				exampleItems: buildExampleItemsPhrase({
					names: orderedNames,
					totalItems: orderedNames.length,
					limit: limits.exampleItems
				})
			};
		}
		const examples = {
			large: examplesLimits({ exampleItems: 4 }),
			mid: examplesLimits({ exampleItems: 3 }),
			small: examplesLimits({ exampleItems: 2 })
		};
		const description: string = closestByLength([
			`Discover ${location.city.label}'s hidden gems with fascinating local history, ${examples.large.exampleItems}.`,
			`Discover ${location.city.label}'s hidden gems with fascinating local history, ${examples.mid.exampleItems}.`,
			`Discover ${location.city.label}'s hidden gems with fascinating local history, ${examples.small.exampleItems}.`
		]);

		return {
			title: `${location.city.label} Hidden Gems & Local Secrets: ${shortestName} & More`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdTouristAttractionsCollection = $derived.by(() => {
		const json = data.output.jsonLdTouristAttractionsCollection;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristAttractionsCollection]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Hidden Gems & Local Secrets in ${location.city.label}`}
	description={`Step off the tourist trail and experience the authentic side of the city. We've bypassed the crowded landmarks to uncover the best-kept local secrets—from quiet neighborhood courtyards to underground art scenes. Dive into the forgotten histories and unique stories that make these spots truly special.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: items.flatMap((item) => item.primary?.place?.coordinates ?? item.feature.place?.coordinates ?? []),
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="flex flex-col gap-12 lg:gap-16">
		{#each items as item, i (item.feature.id)}
			<li>
				{@render ItemRow(item, i === 0)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet ItemRow(item: HiddenGem, isFirstEager: boolean)}
	{const images = $derived<ImageDetails[]>(item.feature.images.length > 0 ? item.feature.images : item.primary?.images || [])}

	<article
		class="flex scroll-mt-28 flex-col gap-8 rounded-3xl border border-base-content/10 bg-base-100 p-6 shadow-sm transition-all hover:border-base-content/20 md:p-8"
		id={item.feature.slug}
	>
		<TextImageSidesStructure englishName={item.feature.englishName} {images} eagerLoadImages={isFirstEager}>
			{#snippet TextContentSnippet()}
				<div class="flex flex-col gap-3 border-b border-base-content/5 pb-4 sm:flex-row sm:items-start sm:justify-between">
					<div class="flex flex-col gap-1">
						{@render ItemTitle({ englishName: item.feature.englishName })}
						{@render ItemNativeName({
							nativeName: item.feature.nativeName,
							language: output.countryLanguage
						})}
					</div>

					<div class="shrink-0 pt-1">
						{@render ItemQuickFacts({
							facts: [
								{
									text: `${secondsToFormattedHourMinutes(item.durationMinutes * 60)}`,
									title: 'Recommended visit duration',
									Icon: IconClockTimeFourOutline
								}
							]
						})}
					</div>
				</div>

				{@render ItemDescription({ text: item.feature.description })}

				{#if shouldDisplayStoryElements(item)}
					<div class="mt-2 flex flex-col gap-4 border-t border-base-content/10 pt-6">
						<h3 class="text-sm font-bold tracking-widest text-base-content/70">HISTORY & HIGHLIGHTS</h3>
						<Story story={item.story} events={item.events} storyMaxLines={4} />
					</div>
				{/if}

				<div>
					{@render ItemViewPrimary({ primary: item.primary, location })}
				</div>
			{/snippet}
		</TextImageSidesStructure>

		<div class="border-t border-base-content/10 pt-6">
			<PlaceExtras
				name={item.feature.englishName}
				place={item.primary?.place ?? item.feature.place}
				videos={item.feature.videos}
				typeMapPoints={output.typeMapPoints}
			/>
		</div>
	</article>
{/snippet}
